import { GetAgentQuery } from '@/api/agents/queries/get-agent-query'
import { GetBrandQuery, IBrand } from '@/api/brands/queries/get-brand-query'
import { GetBrandsQuery } from '@/api/brands/queries/get-brands-query'
import type { IAgentDetailResponse } from '@/features/agents/types/agent-types'
import { IApi } from '@/setup/api-client'
import { useApiMutation } from '@/utils/api/api-mutation'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type {
    CreateEmptyBrandBody,
    CreateEmptyBrandData,
} from 'api/dist/routes/organization/[organization_id]/brands/empty'

export type ICreateEmptyBrandResponse = CreateEmptyBrandData

export type ICreateEmptyBrandMutationParams = {
    organizationId: string
    body?: CreateEmptyBrandBody
}

export class CreateEmptyBrandMutation {
    static async call(api: IApi, params: ICreateEmptyBrandMutationParams) {
        return api
            .post<ICreateEmptyBrandResponse, CreateEmptyBrandBody>(
                `/organization/${params.organizationId}/brands/empty`,
                params.body ?? {}
            )
            .then((res) => res.data)
    }
}

/**
 * Create-brand mutation. On success the caller navigates client-side to the
 * new brand's canonical (UUID) URL, so we seed the react-query caches under
 * that identity here — the destination renders instantly from cache and never
 * observes a transient 404 while replicas/org caches catch up with the write.
 */
export function useCreateEmptyBrandMutation() {
    const { api, apiParams, qc } = useApiToolbox()

    return useApiMutation({
        mutationFn: (body?: CreateEmptyBrandBody) => {
            if (!apiParams.organizationId) return Promise.reject('Workspace not found')

            return CreateEmptyBrandMutation.call(api, {
                organizationId: apiParams.organizationId,
                body,
            })
        },
        onSuccess: (data) => {
            const organizationId = apiParams.organizationId
            if (!organizationId || !data?.emptyBrand?.uuid) return

            const brand = data.emptyBrand as IBrand

            // Brand detail under the canonical identity (uuid).
            qc.setQueryData(GetBrandQuery.getKey({ organizationId, brandSlug: brand.uuid }, false), brand)

            // Brands list: prepend the new brand if it isn't there yet (the
            // server may return an EXISTING placeholder — one-empty-brand rule
            // — which is usually in the list already).
            qc.setQueryData<IBrand[]>(GetBrandsQuery.getKey({ organizationId }), (prev) => {
                if (!prev) return prev
                if (prev.some((b) => b.uuid === brand.uuid)) return prev
                return [brand, ...prev]
            })

            // Agent detail (with uiMessages — the backend reloads the agent
            // from primary specifically so this seed can render the initial
            // user message on first paint, instead of flashing the empty
            // "What do you want to build?" template while GET /agents/{uuid}
            // is in flight).
            if (data.agent?.uuid) {
                qc.setQueryData(
                    GetAgentQuery.getKey({
                        organizationId,
                        brandSlug: brand.uuid,
                        agentUuid: data.agent.uuid,
                    }),
                    data.agent as unknown as IAgentDetailResponse
                )
            }
        },
    })
}
